import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import PatentCard from '../components/PatentCard';
import './PortfolioNavigation.css';

function PortfolioNavigation() {
  const [activeTab, setActiveTab] = useState('search'); // search, map, infringement
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search tab state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [semanticRerank, setSemanticRerank] = useState(true);
  const [searchLimit, setSearchLimit] = useState(50);

  // Selected patent for navigation
  const [selectedPatent, setSelectedPatent] = useState(null);
  const [patentFacets, setPatentFacets] = useState(null);
  const [dimension, setDimension] = useState('overall');
  const [neighbours, setNeighbours] = useState([]);

  // Map tab state
  const [mapData, setMapData] = useState([]);
  const [mapFilters, setMapFilters] = useState({});
  const [availableFacets, setAvailableFacets] = useState(null);

  // Infringement tab state
  const [specText, setSpecText] = useState('');
  const [infringementResults, setInfringementResults] = useState([]);
  const [topK, setTopK] = useState(10);

  const dimensions = [
    { value: 'overall', label: 'Overall Similarity' },
    { value: 'problem', label: 'Problem Space' },
    { value: 'solution', label: 'Solution Space' },
    { value: 'how_it_works', label: 'How It Works' },
    { value: 'products_services', label: 'Products/Services' },
    { value: 'cpc', label: 'CPC Codes' }
  ];

  // Search and navigate
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${config.apiBaseUrl}/navigation/search`, {
        query: searchQuery,
        semantic_rerank: semanticRerank,
        limit: searchLimit
      });
      setSearchResults(response.data.results || []);
      
      if (response.data.results.length === 0) {
        setError('No results found. Try different search terms.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePatentSelect = async (patent) => {
    setSelectedPatent(patent);
    setLoading(true);
    setError(null);

    try {
      // Fetch facets for selected patent
      const facetsResponse = await axios.get(
        `${config.apiBaseUrl}/navigation/patent/${patent.patent_id}/facets`
      );
      setPatentFacets(facetsResponse.data.facets);

      // Fetch neighbours for current dimension
      const neighboursResponse = await axios.get(
        `${config.apiBaseUrl}/navigation/patent/${patent.patent_id}/neighbours`,
        { params: { dimension, limit: 10 } }
      );
      setNeighbours(neighboursResponse.data.neighbours || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load patent details');
      console.error('Patent selection error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDimensionChange = async (newDimension) => {
    if (!selectedPatent) return;

    setDimension(newDimension);
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/navigation/patent/${selectedPatent.patent_id}/neighbours`,
        { params: { dimension: newDimension, limit: 10 } }
      );
      setNeighbours(response.data.neighbours || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load neighbours');
      console.error('Dimension change error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load map data
  const loadMapData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${config.apiBaseUrl}/navigation/map/coordinates`);
      setMapData(response.data.patents || []);
      
      // Extract available facets for filtering (if provided by API)
      // For now, we'll need to implement facet extraction logic
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load map data');
      console.error('Map loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMapFilter = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${config.apiBaseUrl}/navigation/map/filters`, {
        projection: 'overall_v1',
        filters: mapFilters
      });
      setMapData(response.data.patents || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to filter map');
      console.error('Map filter error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Infringement search
  const handleInfringementSearch = async () => {
    if (!specText.trim()) {
      setError('Please enter a specification text');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${config.apiBaseUrl}/navigation/infringement/search`, {
        spec_text: specText,
        top_k: topK
      });
      setInfringementResults(response.data.candidates || []);
      
      if (response.data.candidates.length === 0) {
        setError('No potentially infringing patents found.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Infringement search failed');
      console.error('Infringement search error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load map data when map tab is activated
  useEffect(() => {
    if (activeTab === 'map' && mapData.length === 0) {
      loadMapData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="portfolio-navigation-page">
      <section className="card hero">
        <div>
          <p className="eyebrow">Portfolio Navigation</p>
          <h1>Multi-Dimensional Patent Exploration</h1>
          <p className="subtitle">
            Search and navigate by problem/solution spaces, visualize portfolio clusters, 
            and identify potential infringement matches.
          </p>
        </div>
      </section>

      <div className="navigation-tabs">
        <button
          className={`tab ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          🔍 Search & Navigate
        </button>
        <button
          className={`tab ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => setActiveTab('map')}
        >
          🗺️ Portfolio Map
        </button>
        <button
          className={`tab ${activeTab === 'infringement' ? 'active' : ''}`}
          onClick={() => setActiveTab('infringement')}
        >
          ⚖️ Infringement Analysis
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Search & Navigate Tab */}
      {activeTab === 'search' && (
        <div className="tab-content">
          <section className="card">
            <h2>Search Patents</h2>
            <div className="search-form">
              <div className="form-row">
                <label>Search Query</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter keywords (e.g., '5G beamforming optimization')"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              
              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={semanticRerank}
                    onChange={(e) => setSemanticRerank(e.target.checked)}
                  />
                  <span>Use semantic re-ranking</span>
                </label>
                
                <div className="form-row inline">
                  <label>Max Results:</label>
                  <select value={searchLimit} onChange={(e) => setSearchLimit(Number(e.target.value))}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="search-results">
                <h3>Results ({searchResults.length})</h3>
                <div className="results-list">
                  {searchResults.map((result) => (
                    <div
                      key={result.patent_id}
                      className={`result-card ${selectedPatent?.patent_id === result.patent_id ? 'selected' : ''}`}
                      onClick={() => handlePatentSelect(result)}
                    >
                      <h4>{result.title}</h4>
                      <p className="patent-id">{result.patent_id}</p>
                      <p className="summary">{result.summary}</p>
                      {result.score && (
                        <span className="score">Score: {result.score.toFixed(2)}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {selectedPatent && (
            <>
              <section className="card">
                <h2>Patent Details: {selectedPatent.patent_id}</h2>
                <h3>{selectedPatent.title}</h3>
                
                {patentFacets && (
                  <div className="facets-display">
                    <h4>Patent Classifications</h4>
                    {Object.entries(patentFacets).map(([type, facets]) => (
                      facets.length > 0 && (
                        <div key={type} className="facet-group">
                          <strong>{type.charAt(0).toUpperCase() + type.slice(1)}:</strong>
                          <div className="facet-tags">
                            {facets.map((facet, idx) => (
                              <span key={idx} className="facet-tag">
                                {facet.label}
                                {facet.confidence && (
                                  <span className="confidence">({facet.confidence}%)</span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </section>

              <section className="card">
                <h2>Navigate by Dimension</h2>
                <div className="dimension-selector">
                  {dimensions.map((dim) => (
                    <button
                      key={dim.value}
                      className={`btn ${dimension === dim.value ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleDimensionChange(dim.value)}
                    >
                      {dim.label}
                    </button>
                  ))}
                </div>

                {neighbours.length > 0 && (
                  <div className="neighbours-list">
                    <h3>Related Patents ({dimension})</h3>
                    {neighbours.map((neighbour) => (
                      <div
                        key={neighbour.patent_id}
                        className="neighbour-card"
                        onClick={() => handlePatentSelect(neighbour)}
                      >
                        <div className="neighbour-header">
                          <h4>{neighbour.title}</h4>
                          <span className="score">
                            Score: {neighbour.score.toFixed(2)} (Rank {neighbour.rank})
                          </span>
                        </div>
                        <p className="patent-id">{neighbour.patent_id}</p>
                        {neighbour.shared_facets && neighbour.shared_facets.length > 0 && (
                          <div className="shared-facets">
                            <strong>Shared:</strong> {neighbour.shared_facets.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}

      {/* Portfolio Map Tab */}
      {activeTab === 'map' && (
        <div className="tab-content">
          <section className="card">
            <h2>Portfolio Visualization</h2>
            <p className="subtitle">
              2D visualization of patent portfolio with clustering. 
              <em> (Visualization component to be implemented)</em>
            </p>
            
            <div className="map-placeholder">
              <div className="placeholder-content">
                <h3>📊 Interactive Portfolio Map</h3>
                <p>Total Patents: {mapData.length}</p>
                <p>This will display a scatter plot of patents in 2D space,</p>
                <p>colored by clusters and filterable by facets.</p>
                <p className="hint">
                  Implementation requires a charting library like D3.js or Plotly.js
                </p>
              </div>
            </div>

            {mapData.length > 0 && (
              <div className="map-data-summary">
                <h4>Loaded Patents: {mapData.length}</h4>
                <button
                  className="btn btn-secondary"
                  onClick={() => console.log('Map data:', mapData)}
                >
                  View Data in Console
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Infringement Analysis Tab */}
      {activeTab === 'infringement' && (
        <div className="tab-content">
          <section className="card">
            <h2>Spec-Based Infringement Search</h2>
            <p className="subtitle">
              Upload or paste a product specification to find potentially infringing patents.
            </p>

            <div className="infringement-form">
              <div className="form-row">
                <label>Specification Text</label>
                <textarea
                  value={specText}
                  onChange={(e) => setSpecText(e.target.value)}
                  placeholder="Paste specification text here (e.g., standard excerpts, product descriptions)..."
                  rows={10}
                />
              </div>

              <div className="form-row inline">
                <label>Top Matches:</label>
                <select value={topK} onChange={(e) => setTopK(Number(e.target.value))}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleInfringementSearch}
                disabled={loading}
              >
                {loading ? 'Analyzing...' : 'Find Potential Infringement'}
              </button>
            </div>

            {infringementResults.length > 0 && (
              <div className="infringement-results">
                <h3>Potential Infringement Matches</h3>
                <div className="results-list">
                  {infringementResults.map((result, idx) => (
                    <div key={result.patent_id} className="infringement-card">
                      <div className="match-header">
                        <h4>#{idx + 1} - {result.title}</h4>
                        <span className="match-score">
                          Match Score: {result.score.toFixed(2)}
                        </span>
                      </div>
                      <p className="patent-id">{result.patent_id}</p>
                      <p className="reason">{result.reason}</p>
                      
                      {result.match_details && (
                        <div className="match-details">
                          <div className="detail-row">
                            <span>Embedding Similarity:</span>
                            <strong>{result.match_details.embedding_similarity?.toFixed(2) || 'N/A'}</strong>
                          </div>
                          <div className="detail-row">
                            <span>Feature Overlap:</span>
                            <strong>{result.match_details.feature_overlap?.toFixed(2) || 'N/A'}</strong>
                          </div>
                          {result.match_details.matched_terms && (
                            <div className="matched-terms">
                              <strong>Matched Terms:</strong>
                              <div className="terms-list">
                                {result.match_details.matched_terms.map((term, i) => (
                                  <span key={i} className="term-tag">{term}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <button
                        className="btn btn-small"
                        onClick={() => handlePatentSelect(result)}
                      >
                        Navigate from this patent →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default PortfolioNavigation;
